/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect } from 'react';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Select, Table } from 'antd';
import { modalMethod } from 'components';
import {
  useGetPartnerContracts,
  useSaveCertificateIssuance,
  useCheckCertificateIssuance,
  useDeleteAssigneeManager,
  useMeInfo,
  type TAssigneeManager
} from 'hooks';
import { getLocalStorage, setLocalStorage, setPassCertification } from 'utils';
import { FlexBoxCenter } from 'styles';

import AccountBankModal from './modal/AccountBankModal';
import AccountManagerModal from './modal/AccountManagerModal';
import {
  TableTitle,
  // columns
  partnerContractColumns,
  bizAccountColumns,
  certificateColumns,
  assigneeManagerColumns,
  partnerColumns
} from './schema';

import { WhiteWrapper, SelectWrapper, TableWrapper, ActionButton } from './styles';

const customerMap = {
  '0000': `[등록성공]\n공동인증서 등록이 완료 되었습니다.`,
  '0001': `[등록실패]\n다른 사업자등록번호로 발급된 인증서입니다.\n동일한 사업자등록번호의 인증서를 등록해주세요.`,
  '0002': `[등록실패]\n세금계산서를 발행할 수 없는 인증서입니다.\n사업자용 세금계산서 인증서를 등록해주세요.`,
  '0003': `[등록실패]\n세금계산서를 발행할 수 없는 인증서입니다.\n사업자용 세금계산서 인증서를 등록해주세요.`,
  '9999': `[등록실패]\n인증서를 등록하지 못하였습니다.\n다시 시도해주세요.`
};

const PartnerContractsPage = () => {
  const navigate = useNavigate();
  const { meInfo } = useMeInfo();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedPartnerContract, setSelectedPartnerContract] = React.useState<number>();
  const [assigneeManager, setAssigneeManager] = React.useState<TAssigneeManager>();
  const [isAccountBankModal, setIsAccountBankModal] = React.useState<boolean>(false);
  const [isAccountManagerModal, setIsAccountManagerModal] = React.useState<boolean>(false);

  const { mutateAsync: getPartnerContracts, data: partnerContracts } = useGetPartnerContracts();
  const { mutateAsync: saveCertificateIssuance } = useSaveCertificateIssuance();
  const { mutateAsync: checkCertificateIssuance } = useCheckCertificateIssuance(
    (result) => {
      searchParams.delete('code');
      searchParams.delete('message');
      searchParams.delete('customerKey');
      modalMethod.info(<div style={{ whiteSpace: 'break-spaces' }}>{customerMap[result?.code || '9999']}</div>);
      setSearchParams(searchParams);
    },
    (error) => {
      modalMethod.info(<div style={{ whiteSpace: 'break-spaces' }}>{customerMap['9999']}</div>);
    }
  );
  const { mutateAsync: deleteAssigneeManager } = useDeleteAssigneeManager();

  const selectOptions = partnerContracts?.map((item) => ({ value: item.partnerContractIdx, label: item.contractName })) || [];
  const dataSource = React.useMemo(() => {
    return partnerContracts?.find((item) => item.partnerContractIdx === selectedPartnerContract);
  }, [partnerContracts, selectedPartnerContract]);

  const handleAccountBankModal = () => setIsAccountBankModal(!isAccountBankModal);
  const handleAccountManagerModal = (_, values?: TAssigneeManager, isRefresh?: boolean) => {
    setAssigneeManager(!isAccountManagerModal ? values : undefined);
    setIsAccountManagerModal(!isAccountManagerModal);

    if (isRefresh) {
      getPartnerContracts();
    }
  };

  const handleDeleteAssigneeManager = async (_, values?: TAssigneeManager) => {
    const confirmResult = await modalMethod.confirm(`정산 담당자 정보를 삭제 하시겠습니까?`, {
      cancelText: '닫기',
      okText: '삭제',
      okButtonProps: {
        style: { color: '#FA503C' }
      }
    });
    if (confirmResult && dataSource) {
      await deleteAssigneeManager({ partnerContractIdx: dataSource.partnerContractIdx, assigneeManagerIdx: values.managerIdx });
      await modalMethod.info('정산 담당자 정보 삭제가 완료되었습니다.');
    }
  };

  useEffect(() => {
    if (!meInfo?.admin?.isCertification) {
      setPassCertification(meInfo?.admin?.id, false);
      navigate('/');
    } else {
      getPartnerContracts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!meInfo?.admin?.isCertification) return;
    if (partnerContracts?.length) {
      const idx = getLocalStorage('partnerContractIdx');
      setSelectedPartnerContract(idx || partnerContracts[0].partnerContractIdx);
    } else if (partnerContracts && !partnerContracts.length) {
      modalMethod.confirm(`내 계약정보가 존재 하지 않아 페이지 진입이 불가합니다.`, {
        cancelButtonProps: { style: { display: 'none' } },
        onOk: () => navigate('/')
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerContracts]);

  useEffect(() => {
    const code = searchParams.get('code');
    const customerKey = searchParams.get('customerKey');

    if (code && customerKey) {
      window.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectChange = (value: number) => {
    setSelectedPartnerContract(value);
    setLocalStorage('partnerContractIdx', value);
    getPartnerContracts();
  };

  const showCertificateIssuance = () => {
    modalMethod.info(
      <div style={{ whiteSpace: 'break-spaces' }}>{`새창에서 공동인증서 등록이 완료된 다음\n[닫기] 버튼을 클릭해주세요`}</div>,
      {
        okText: '닫기',
        onOk: async () => {
          const result = await getPartnerContracts();
          const target = result?.find(({ partnerContractIdx }) => partnerContractIdx === selectedPartnerContract);
          checkCertificateIssuance({ bizAccountIdx: target?.bizAccountIdx, partnerContractIdx: target?.partnerContractIdx });
        }
      }
    );
  };

  const handleCertificateIssuance = async () => {
    if (dataSource) {
      const { certificateIssuedDate, certificateExpiredDate } = dataSource;
      const getCertificateStatus = () => {
        if (!certificateIssuedDate && !certificateExpiredDate) return 'NONE';
        if (certificateIssuedDate && !certificateExpiredDate) return 'CONFIRM';
        if (certificateIssuedDate && certificateExpiredDate && moment().isAfter(certificateExpiredDate)) return 'EXPIRED';
        return 'CONFIRM';
      };

      const status = getCertificateStatus();

      const content =
        status === 'NONE'
          ? `공동인증서 등록은\n세금계산서 발급 대행업체에서 진행합니다.`
          : `이미 공동인증서가 등록되었습니다.\n다시 인증서를 등록하시겠습니까?`;
      const okText = status === 'NONE' ? `등록하기` : `재등록하기`;
      const confirmResult = await modalMethod.confirm(<div style={{ whiteSpace: 'break-spaces' }}>{content}</div>, {
        cancelText: '닫기',
        okText
      });

      if (!confirmResult) return;
      const params = { partnerContractIdx: selectedPartnerContract, bizAccountIdx: dataSource?.bizAccountIdx };
      const result = await saveCertificateIssuance(params);
      showCertificateIssuance();
      window.open(result.url, 'contracts_certificateModal', 'width=800, height=600');
    }
  };

  const assigneeManagers = dataSource?.assigneeManagers?.slice()?.sort((a, b) => {
    if (a.isReceivedTaxInvoice !== b.isReceivedTaxInvoice) {
      return b.isReceivedTaxInvoice ? 1 : -1;
    }
    return a.managerIdx - b.managerIdx;
  });

  const bankAccount = {
    bankCode: dataSource?.accountBankCode,
    bankName: dataSource?.accountBankName,
    bankAccount: dataSource?.accountBankAccountNumber,
    bankAccountHolder: dataSource?.accountBankAccountHolder,
    bankAccountFile: undefined
  };

  return (
    <div>
      <WhiteWrapper>
        <SelectWrapper>
          <span style={{ fontSize: '16px', fontWeight: '700', paddingRight: '10px' }}>계약서 선택</span>
          <Select value={selectedPartnerContract} options={selectOptions} onChange={handleSelectChange} />
        </SelectWrapper>
      </WhiteWrapper>

      <WhiteWrapper>
        <FlexBoxCenter direction="column" gap="12px">
          <TableWrapper>
            <TableTitle
              label="계약서 기본 정보"
              action={<ActionButton onClick={handleAccountBankModal}>계좌 정보 수정 요청</ActionButton>}
            />
            <Table columns={partnerContractColumns} dataSource={[dataSource]} pagination={false} />
          </TableWrapper>
          <TableWrapper>
            <TableTitle label="사업자 정보" description="사업자정보 수정 요청은 하단에 1:1 문의를 이용해주세요" />
            <Table columns={bizAccountColumns} dataSource={[dataSource]} pagination={false} />
          </TableWrapper>
          <TableWrapper>
            <TableTitle
              label="인증서 정보"
              action={
                dataSource?.taxableEvidenceType === 'ELECTRONIC_CERTIFICATE' ? (
                  <ActionButton onClick={handleCertificateIssuance}>공동 인증서 등록</ActionButton>
                ) : null
              }
            />
            <Table columns={certificateColumns} dataSource={[dataSource]} pagination={false} />
          </TableWrapper>
          <TableWrapper>
            <TableTitle
              label="정산 담당자 정보"
              action={<ActionButton onClick={handleAccountManagerModal}>담당자 등록</ActionButton>}
            />
            <Table
              columns={assigneeManagerColumns({
                onManagerModal: handleAccountManagerModal,
                onDeleteAssigneeManager: handleDeleteAssigneeManager
              })}
              dataSource={assigneeManagers}
              pagination={false}
              locale={{ emptyText: '정산 담당자 정보가 없습니다.' }}
            />
          </TableWrapper>
          <TableWrapper>
            <TableTitle label="연결 제휴점 정보" description={`연결 제휴점 갯수 : ${dataSource?.partners?.length || 0}개`} />
            <Table
              columns={partnerColumns}
              dataSource={dataSource?.partners}
              locale={{ emptyText: '연결 제휴점 정보가 없습니다.' }}
              pagination={false}
            />
          </TableWrapper>
        </FlexBoxCenter>

        <AccountBankModal
          open={isAccountBankModal}
          partnerContractIdx={selectedPartnerContract}
          bankAccount={bankAccount}
          onCancel={handleAccountBankModal}
        />
        <AccountManagerModal
          open={isAccountManagerModal}
          partnerContractIdx={selectedPartnerContract}
          assigneeManager={assigneeManager}
          isReceivedLimits={assigneeManagers?.filter((item) => item?.isReceivedTaxInvoice).length >= 1}
          onCancel={handleAccountManagerModal}
        />
      </WhiteWrapper>
    </div>
  );
};

export default PartnerContractsPage;
